import { create } from 'zustand';
import { createDebouncedJSONStorage } from 'zustand-debounce';
import { persist } from 'zustand/middleware';
import { DatasetType, MarkerStore } from '../types/marker';
import indexedDbStorage from '../utils/indexeddb-zustand-store';

const updateMarkerPanelValue = <TValue>(
  markers: MarkerStore['markers'],
  markerId: string,
  field:
    | 'waterDepth'
    | 'cellSize'
    | 'landRelation'
    | 'isDatasetOffline'
    | 'isDatasetHasReport',
  panelName: string,
  value: TValue
) => {
  const markerToUpdate = markers.find((marker) => marker.id === markerId);

  if (!markerToUpdate) {
    return markers;
  }

  const currentValues = markerToUpdate[field] || {};
  const updatedPanelData = {
    ...currentValues,
    [panelName]: value,
  };

  return markers.map((marker) =>
    marker.id === markerId ? { ...marker, [field]: updatedPanelData } : marker
  );
};

export const storageName = 'MOOD_marker_store';

export const useMarkerStore = create<MarkerStore>()(
  persist(
    (set, get) => ({
      markers: [],
      activeMarkerId: null,
      activeMarker: () => get().markers.find((marker) => marker.active) || null,
      isAddMarkerMode: false,

      addMarker: (marker) =>
        set((state) => ({
          markers: [
            ...state.markers.map((m) => ({ ...m, active: false })),
            marker,
          ],
          activeMarkerId: marker.id,
        })),
      deleteMarker: (markerId) =>
        set((state) => ({
          markers: state.markers.filter((marker) => marker.id !== markerId),
          activeMarkerId: null,
        })),

      updateMarker: (markerId, updates) => {
        return set((state) => ({
          markers: state.markers.map((m) =>
            m.id === markerId ? { ...m, ...updates } : m
          ),
        }));
      },

      setActiveMarker: (markerId) =>
        set((state) => {
          const updatedMarkers = state.markers.map((marker) => ({
            ...marker,
            active: marker.id === markerId,
          }));

          return {
            markers: updatedMarkers,
            activeMarkerId: markerId,
          };
        }),
      setAddMarkerMode: (status) => set({ isAddMarkerMode: status }),
      setActiveKey: (markerId, metadataType) =>
        set((state) => ({
          markers: state.markers.map((marker) =>
            marker.id === markerId ? { ...marker, key: metadataType } : marker
          ),
        })),

      setResetMarker: () =>
        set(() => ({
          markers: [],
          activeMarkerId: null,
          isAddMarkerMode: false,
        })),

      setDownloadPage: (markerId, panelName, value) =>
        set((state) => {
          const updatedMarkers = state.markers.map((marker) => {
            if (marker.id !== markerId) return marker;

            const currentDownloadPages = Array.isArray(marker.downloadPage)
              ? [...marker.downloadPage]
              : [];

            const existingIndex = currentDownloadPages.findIndex(
              (item) => item && item.radioKey === panelName
            );

            let newDownloadPages;

            if (existingIndex >= 0) {
              newDownloadPages = [...currentDownloadPages];
              newDownloadPages[existingIndex] = value;
            } else {
              newDownloadPages = [...currentDownloadPages, value];
            }

            return {
              ...marker,
              downloadPage: newDownloadPages,
            };
          });

          return {
            markers: updatedMarkers,
          };
        }),

      setWaterDepth: (
        markerId: string,
        panelName: string,
        value: string | number | null
      ) => {
        set((state) => {
          return {
            markers: updateMarkerPanelValue(
              state.markers,
              markerId,
              'waterDepth',
              panelName,
              value === null ? '-' : value
            ),
          };
        });
      },

      setCellSize: (
        markerId: string,
        panelName: string,
        value: string | number | null
      ) => {
        set((state) => {
          return {
            markers: updateMarkerPanelValue(
              state.markers,
              markerId,
              'cellSize',
              panelName,
              value === null ? '-' : value
            ),
          };
        });
      },

      setLandRelation: (markerId, panelName, value) => {
        set((state) => ({
          markers: updateMarkerPanelValue(
            state.markers,
            markerId,
            'landRelation',
            panelName,
            value === null ? '-' : value
          ),
        }));
      },

      setDatasetOffline: (
        markerId: string,
        panelName: string,
        value: boolean
      ) => {
        set((state) => {
          return {
            markers: updateMarkerPanelValue(
              state.markers,
              markerId,
              'isDatasetOffline',
              panelName,
              value
            ),
          };
        });
      },

      setDatasetReport: (
        markerId: string,
        panelName: string,
        value: boolean
      ) => {
        set((state) => {
          return {
            markers: updateMarkerPanelValue(
              state.markers,
              markerId,
              'isDatasetHasReport',
              panelName,
              value
            ),
          };
        });
      },

      setActiveDatasetKey: (
        markerId: string,
        datasetKey: DatasetType,
        newActiveDataset: string
      ) => {
        set((state) => ({
          markers: state.markers.map((marker) =>
            marker.id === markerId
              ? {
                  ...marker,
                  activeDatasetKey: datasetKey,
                  dataset: newActiveDataset,
                }
              : marker
          ),
        }));
      },

      setActivePanel: (markerId: string, datasetKey: DatasetType | string) => {
        set((state) => ({
          markers: state.markers.map((marker) =>
            marker.id === markerId
              ? { ...marker, activePanel: datasetKey }
              : marker
          ),
        }));
      },
    }),
    {
      name: storageName,
      storage: createDebouncedJSONStorage(indexedDbStorage, {
        debounceTime: 500,
      }),
      version: 2,
      partialize: (state) => ({
        markers: state.markers,
        activeMarkerId: state.activeMarkerId,
        isAddMarkerMode: state.isAddMarkerMode,
      }),
    }
  )
);

export const useActiveMarker = () => {
  return useMarkerStore((state) => {
    const id = state.activeMarkerId;
    return id ? state.markers.find((m) => m.id === id) || null : null;
  });
};

export const isStoreEmpty = (state: MarkerStore) => {
  return (
    state.markers.length === 0 &&
    state.activeMarkerId === null /*&& !state.isAddMarkerMode*/
  );
};
